import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Text,
  Stack,
  Group,
  Badge,
  Paper,
  SimpleGrid,
  Alert,
  Box,
  Select,
  MultiSelect,
  Divider,
  Grid,
  Skeleton,
  Button,
  Pagination
} from '@mantine/core';
import { IconCheck, IconChevronLeft, IconChevronRight, IconSortAscending } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PageHeader } from '../common/PageHeader';
import { SectionHeader } from '../common/SectionHeader';
import {
  COMPONENT_PROJECTS,
  findComponentProjectById
} from '../../config/initiativeLeadsMapping';
import dayjs from 'dayjs';
import { getStatusColor, getStatusLabel, submissionStateToDisplayStatus } from '../../types/status';
import {
  getInitiativeLeads,
  getOutstandingItems,
  type OutstandingBucket,
  type OutstandingItemsSort,
  type SubawardeeReportRow
} from '../../api/admin';
import { extractErrorMessage } from '../../utils/notifications';
import { withAdminSubawardeeQuery } from '../../utils/adminSubawardeePaths';

/** Fixed page size for outstanding-items API (no user control). */
const OUTSTANDING_ITEMS_PAGE_SIZE = 5;

const OUTSTANDING_SKELETON_CARD_COUNT = 3;

/** Same height for each “inner row” placeholder in both columns */
const SKELETON_ROW_HEIGHT = 84;

function OutstandingSectionToolbarSkeleton() {
  return (
    <Group position="apart" align="flex-end" spacing="md" noWrap aria-hidden>
      <Skeleton
        height={20}
        radius="sm"
        sx={{ flex: 1, minWidth: 120, maxWidth: 420 }}
      />
      <Group spacing="sm" noWrap>
        <Skeleton height={36} width={220} radius="sm" />
        <Skeleton height={36} width={280} radius="sm" />
        <Skeleton height={36} width={250} radius="sm" />
      </Group>
    </Group>
  );
}

function OutstandingCardSkeleton() {
  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      aria-hidden
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      })}
    >
      <Stack spacing="md" sx={{ flex: 1 }}>
        <Box>
          <Skeleton height={22} width="42%" mb={10} radius="sm" />
          <Group position="apart" noWrap>
            <Skeleton height={14} width="36%" radius="sm" />
            <Stack spacing={6} align="flex-end" sx={{ minWidth: 100 }}>
              <Skeleton height={14} width={100} radius="sm" />
              <Skeleton height={18} width={72} radius="sm" />
            </Stack>
          </Group>
        </Box>

        <Grid sx={{ flex: 1, alignItems: 'stretch' }} gutter={48} mt="sm">
          <Grid.Col span={6}>
            <Stack spacing="sm">
              <Box>
                <Group position="apart" align="center" noWrap>
                  <Skeleton height={14} width={130} radius="sm" />
                  <Skeleton height={12} width={90} radius="sm" />
                </Group>
                <Divider mt="xs" />
              </Box>
              <Stack spacing="xs">
                <Skeleton height={SKELETON_ROW_HEIGHT} radius="sm" />
                <Skeleton height={SKELETON_ROW_HEIGHT} radius="sm" />
              </Stack>
            </Stack>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack spacing="sm">
              <Box>
                <Group position="apart" align="center" noWrap>
                  <Skeleton height={14} width={150} radius="sm" />
                  <Skeleton height={12} width={70} radius="sm" />
                </Group>
                <Divider mt="xs" />
              </Box>
              <Stack spacing="xs">
                <Skeleton height={SKELETON_ROW_HEIGHT} radius="sm" />
                <Skeleton height={SKELETON_ROW_HEIGHT} radius="sm" />
              </Stack>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  );
}

function mergeSubawardeeReports(bucket: OutstandingBucket): SubawardeeReportRow[] {
  const rows: SubawardeeReportRow[] = [];
  for (const block of bucket.reports) {
    rows.push(...block.subawardee_reports);
  }
  return rows;
}

function isActionableSubreport(row: SubawardeeReportRow): boolean {
  return row.submission_status.state.toLowerCase() !== 'approved';
}

interface QuarterlyReportSummaryProps {
  bucket: OutstandingBucket;
  onViewReport: (reportId: string, subawardee: string) => void;
  onViewMilestone: (subawardee: string) => void;
}

const milestoneStatusConfig: Record<
  string,
  { label: string; color: string }
> = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

function QuarterlyReportSummary({
  bucket,
  onViewReport,
  onViewMilestone
}: QuarterlyReportSummaryProps) {
  const reportingPeriod = bucket.reporting_period;
  const mergedSubs = mergeSubawardeeReports(bucket);
  const project = findComponentProjectById(bucket.component_project_id);

  const getUrgencyInfo = () => {
    const daysUntilDue = dayjs(reportingPeriod.due_date)
      .startOf('day')
      .diff(dayjs().startOf('day'), 'day');

    if (daysUntilDue >= 0 && daysUntilDue <= 7) {
      return {
        label: `Due in ${daysUntilDue} days`,
        color: 'blue',
        variant: 'light' as const
      };
    }
    return null;
  };

  const urgencyInfo = getUrgencyInfo();

  const actionableSubawardees = mergedSubs.filter(isActionableSubreport);

  const incompleteMilestones = bucket.milestones.filter(
    (m) => (m.status || 'not_started').toLowerCase() !== 'completed'
  );

  const periodLabel =
    bucket.reports[0]?.reporting_period_label ?? reportingPeriod.reporting_period_label;

  return (
    <Paper
      tabIndex={0}
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={`${project?.name ?? 'Component project'} ${periodLabel}`}
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow:
            theme.colorScheme === 'dark'
              ? '0 0 16px rgba(255, 255, 255, 0.15)'
              : '0 8px 16px rgba(0, 0, 0, 0.1)'
        },
        '&:focus': {
          outline: 'none',
          borderColor: theme.colors.blue[5]
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      })}
    >
      <Stack spacing="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Text weight={600} size="lg">
            {project?.name ?? bucket.component_project_id}
          </Text>
          <Group position="apart" align="center">
            <Text size="sm" color="dimmed">
              {periodLabel}
            </Text>
            <Stack spacing={4} align="flex-end">
              <Text size="sm" color="dimmed">
                Due: {dayjs(reportingPeriod.due_date).format('MMM D, YYYY')}
              </Text>
              {urgencyInfo && (
                <Badge color={urgencyInfo.color} variant={urgencyInfo.variant} size="xs">
                  {urgencyInfo.label}
                </Badge>
              )}
            </Stack>
          </Group>
        </Box>

        <Grid sx={{ flex: 1, alignItems: 'stretch' }} gutter={48} mt="sm">
          <Grid.Col span={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack spacing="sm" sx={{ flex: 1 }}>
                <Box>
                  <Group position="apart" align="center" noWrap>
                    <Text size="sm" weight={600} color="dimmed">
                      Requires Attention
                    </Text>
                    <Text size="xs" color="dimmed" sx={{ textAlign: 'right' }}>
                      {actionableSubawardees.length} need attention
                    </Text>
                  </Group>
                  <Divider mt="xs" />
                </Box>
                {actionableSubawardees.length > 0 ? (
                  <Stack spacing="xs" sx={{ flex: 1 }}>
                    {actionableSubawardees.map((subreport) => {
                      const displayStatus = submissionStateToDisplayStatus(
                        subreport.submission_status.state
                      );
                      return (
                        <Box
                          key={`${subreport.report_id}-${subreport.subawardee}`}
                          role="button"
                          tabIndex={0}
                          aria-label={`Open ${subreport.subawardee} report`}
                          onClick={() => onViewReport(subreport.report_id, subreport.subawardee)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onViewReport(subreport.report_id, subreport.subawardee);
                            }
                          }}
                          sx={(theme) => ({
                            borderRadius: theme.radius.sm,
                            border: `1px solid ${
                              theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
                            }`,
                            backgroundColor:
                              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                            padding: theme.spacing.sm,
                            cursor: 'pointer',
                            transition:
                              'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                              backgroundColor:
                                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                              borderColor:
                                theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[4]
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${
                                theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
                              }`,
                              outlineOffset: 1
                            }
                          })}
                        >
                          <Stack spacing="sm">
                            <Group position="apart" align="flex-start" noWrap>
                              <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" weight={600} lineClamp={1}>
                                  {subreport.subawardee}
                                </Text>
                              </Stack>
                              <Stack spacing={2} align="flex-end" sx={{ minHeight: 36 }}>
                                <Badge
                                  color={getStatusColor(displayStatus)}
                                  variant="light"
                                  size="sm"
                                  sx={{ flexShrink: 0 }}
                                >
                                  {getStatusLabel(displayStatus)}
                                </Badge>
                                <Text size={11} color="dimmed" weight={500} align="right">
                                  {displayStatus === 'pending' &&
                                  subreport.days_until_auto_approval != null &&
                                  subreport.days_until_auto_approval <= 7
                                    ? `Auto-approves in ${subreport.days_until_auto_approval}d`
                                    : '\u00A0'}
                                </Text>
                              </Stack>
                            </Group>
                            <Box sx={{ minHeight: 22, display: 'flex', alignItems: 'center' }}>
                              <Text size="xs" color="dimmed" weight={500}>
                                {subreport.initiative_lead ?? 'Unknown'}
                              </Text>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size="sm" color="dimmed" align="center">
                      No reports require attention
                    </Text>
                  </Box>
                )}
              </Stack>
            </Box>
          </Grid.Col>

          <Grid.Col span={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack spacing="sm" sx={{ flex: 1 }}>
                <Box>
                  <Group position="apart" align="center" noWrap>
                    <Text size="sm" weight={600} color="dimmed">
                      Incomplete Milestones
                    </Text>
                    <Text size="xs" color="dimmed" sx={{ textAlign: 'right' }}>
                      {incompleteMilestones.length} incomplete
                    </Text>
                  </Group>
                  <Divider mt="xs" />
                </Box>
                {incompleteMilestones.length > 0 ? (
                  <Stack spacing="xs" sx={{ flex: 1 }}>
                    {incompleteMilestones.map((milestone) => {
                      const statusKey = (milestone.status || 'not_started').toLowerCase();
                      const config =
                        milestoneStatusConfig[statusKey] ?? milestoneStatusConfig.not_started;
                      return (
                        <Box
                          key={milestone.id}
                          role="button"
                          tabIndex={0}
                          aria-label={`Open ${milestone.title} milestone details`}
                          onClick={() => onViewMilestone(milestone.subawardee)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onViewMilestone(milestone.subawardee);
                            }
                          }}
                          sx={(theme) => ({
                            borderRadius: theme.radius.sm,
                            border: `1px solid ${
                              theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
                            }`,
                            backgroundColor:
                              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                            padding: theme.spacing.sm,
                            cursor: 'pointer',
                            transition:
                              'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                              backgroundColor:
                                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                              borderColor:
                                theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[4]
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${
                                theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
                              }`,
                              outlineOffset: 1
                            }
                          })}
                        >
                          <Stack spacing="sm">
                            <Group position="apart" align="flex-start" noWrap>
                              <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" weight={600} lineClamp={1}>
                                  {milestone.title}
                                </Text>
                                <Text size="xs" color="dimmed" weight={500} lineClamp={1}>
                                  {milestone.subawardee}
                                </Text>
                              </Stack>
                              <Badge color={config.color} variant="light" size="sm" sx={{ flexShrink: 0 }}>
                                {config.label}
                              </Badge>
                            </Group>
                            <Group position="apart" align="center" noWrap>
                              <Text size="xs" color="dimmed" weight={500}>
                                {milestone.initiative_lead ?? 'Unknown'}
                              </Text>
                              <Text size="xs" color="dimmed" weight={500}>
                                Due: {dayjs(milestone.due_date).format('MMM D, YYYY')}
                              </Text>
                            </Group>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size="sm" color="dimmed" align="center">
                      No incomplete milestones
                    </Text>
                  </Box>
                )}
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  );
}

/** Values match `component_project_id` on outstanding buckets (resolved template labels / ids). */
const COMPONENT_PROJECT_FILTER_DATA = [...COMPONENT_PROJECTS]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((p) => ({ value: p.id, label: p.name }));


interface OutstandingPaginationBarProps {
  page: number;
  totalPages: number;
  totalItems: number;
  rangeStart: number;
  rangeEnd: number;
  disabled: boolean;
  onChange: (page: number) => void;
}

function OutstandingPaginationBar({
  page,
  totalPages,
  totalItems,
  rangeStart,
  rangeEnd,
  disabled,
  onChange
}: OutstandingPaginationBarProps) {
  return (
    <Stack align="center" spacing={8} mt="xl" pt="md">
      <Group position="center" noWrap spacing="lg">
        <Button
          variant="subtle"
          color="dark"
          compact
          leftIcon={<IconChevronLeft size={14} />}
          disabled={disabled || page <= 1}
          onClick={() => onChange(page - 1)}
          styles={(theme) => ({
            root: {
              fontWeight: 500,
              color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
              '&:hover': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.fn.rgba(theme.colors.dark[0], 0.1)
                    : theme.fn.rgba(theme.black, 0.05)
              },
              '&[data-disabled]': {
                color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5],
                backgroundColor: 'transparent'
              }
            }
          })}
        >
          Previous
        </Button>

        <Pagination
          total={totalPages}
          value={page}
          onChange={onChange}
          disabled={disabled}
          color="dark"
          size="sm"
          radius="sm"
          withControls={false}
          styles={(theme) => ({
            control: {
              border: 'none',
              backgroundColor: 'transparent',
              fontWeight: 500,
              minWidth: 32,
              height: 32,
              fontSize: theme.fontSizes.sm,
              color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover:not([data-disabled])': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.fn.rgba(theme.colors.dark[0], 0.1)
                    : theme.fn.rgba(theme.black, 0.05),
                color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black
              },
              '&[data-active]': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? `${theme.colors.dark[4]} !important`
                    : `${theme.black} !important`,
                color: `${theme.white} !important`
              },
              '&[data-active]:hover': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? `${theme.colors.dark[3]} !important`
                    : `${theme.fn.darken(theme.black, 0.1)} !important`,
                color: `${theme.white} !important`
              },
              '&[data-disabled]': {
                opacity: 0.35,
                backgroundColor: 'transparent',
                border: 'none'
              }
            }
          })}
        />

        <Button
          variant="subtle"
          color="dark"
          compact
          rightIcon={<IconChevronRight size={14} />}
          disabled={disabled || page >= totalPages}
          onClick={() => onChange(page + 1)}
          styles={(theme) => ({
            root: {
              fontWeight: 500,
              color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
              '&:hover': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.fn.rgba(theme.colors.dark[0], 0.1)
                    : theme.fn.rgba(theme.black, 0.05)
              },
              '&[data-disabled]': {
                color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5],
                backgroundColor: 'transparent'
              }
            }
          })}
        >
          Next
        </Button>
      </Group>

      <Text size="xs" color="dimmed">
        Showing {rangeStart}&ndash;{rangeEnd} of {totalItems}
      </Text>
    </Stack>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<OutstandingItemsSort>('dueDate-asc');
  const [page, setPage] = useState(1);
  const [componentProjectFilter, setComponentProjectFilter] = useState<string[]>([]);
  const [initiativeLeadFilter, setInitiativeLeadFilter] = useState<string | null>(null);
  const outstandingSectionRef = useRef<HTMLDivElement>(null);

  const { data: initiativeLeadFilterData = [] } = useQuery({
    queryKey: ['admin-initiative-leads'],
    queryFn: getInitiativeLeads,
    staleTime: 10 * 60 * 1000
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    requestAnimationFrame(() => {
      outstandingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const componentProjectQueryParam = useMemo(() => {
    if (componentProjectFilter.length === 0) return undefined;
    return componentProjectFilter
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',');
  }, [componentProjectFilter]);

  const handleLeadFilterChange = useCallback((leadId: string | null) => {
    setInitiativeLeadFilter(leadId);
    setPage(1);
  }, []);

  const handleViewReport = (reportId: string, subawardee: string) => {
    navigate(withAdminSubawardeeQuery(`/admin/reports/${reportId}`, subawardee));
  };

  const handleViewMilestones = (subawardee: string) => {
    navigate(`/admin/milestones?subawardee=${encodeURIComponent(subawardee)}`);
  };

  const sortOptions = [
    { value: 'dueDate-asc', label: 'Due Date (Earliest First)' },
    { value: 'dueDate-desc', label: 'Due Date (Latest First)' },
    { value: 'component-asc', label: 'Component Project (A-Z)' },
    { value: 'component-desc', label: 'Component Project (Z-A)' }
  ];

  const {
    data,
    isFetching,
    error,
    isError
  } = useQuery({
    queryKey: ['admin-outstanding-items', sortBy, page, componentProjectQueryParam, initiativeLeadFilter],
    queryFn: () =>
      getOutstandingItems({
        page,
        page_size: OUTSTANDING_ITEMS_PAGE_SIZE,
        sort_by: sortBy,
        ...(componentProjectQueryParam && { component_project: componentProjectQueryParam }),
        ...(initiativeLeadFilter && { initiative_lead: initiativeLeadFilter })
      }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData
  });

  const buckets = data?.items ?? [];
  const pagination = data?.pagination;

  const showOutstandingToolbar = !isFetching && !isError;
  const effectivePageSize = pagination?.page_size ?? OUTSTANDING_ITEMS_PAGE_SIZE;
  const rangeStart =
    pagination && pagination.total_items > 0 ? (page - 1) * effectivePageSize + 1 : 0;
  const rangeEnd =
    pagination && pagination.total_items > 0
      ? Math.min(page * effectivePageSize, pagination.total_items)
      : 0;

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }}>
      <PageHeader
        title="Admin Dashboard"
        subtitle="Monitor component project health, track quarterly report submissions, and manage milestones across all subawardees"
      />

      <Box ref={outstandingSectionRef} mb="xl" sx={{ scrollMarginTop: 24 }}>
        <SectionHeader title="Outstanding Reports & Milestones" />

        {showOutstandingToolbar && (
          <Group position="right" align="flex-end" mb="md" spacing="sm" noWrap>
              <Select
                aria-label="Filter outstanding items by initiative lead"
                placeholder="All initiative leads"
                data={initiativeLeadFilterData}
                value={initiativeLeadFilter}
                onChange={handleLeadFilterChange}
                searchable
                clearable
                maxDropdownHeight={280}
                style={{ width: 220 }}
                size="sm"
                styles={(theme) => {
                  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
                  return {
                    input: {
                      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' }
                    }
                  };
                }}
              />
              <MultiSelect
                aria-label="Filter outstanding items by component project"
                placeholder="All component projects"
                data={COMPONENT_PROJECT_FILTER_DATA}
                value={componentProjectFilter}
                onChange={(next) => {
                  setComponentProjectFilter(next);
                  setPage(1);
                }}
                searchable
                clearable
                maxDropdownHeight={280}
                style={{ width: 280 }}
                size="sm"
                styles={(theme) => {
                  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
                  return {
                    input: {
                      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' }
                    },
                    searchInput: {
                      '&:focus': { outline: 'none', boxShadow: 'none' }
                    }
                  };
                }}
              />
              <Select
                aria-label="Sort outstanding items"
                value={sortBy}
                onChange={(value) => {
                  if (value) {
                    setSortBy(value as OutstandingItemsSort);
                    setPage(1);
                  }
                }}
                data={sortOptions}
                icon={<IconSortAscending size={16} />}
                placeholder="Sort by..."
                style={{ width: 250 }}
                size="sm"
                styles={(theme) => {
                  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
                  return {
                    input: {
                      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' }
                    }
                  };
                }}
              />
          </Group>
        )}

        {isError && (
          <Alert title="Could not load outstanding items" color="red" mb="md">
            {extractErrorMessage(error, 'Unable to fetch admin dashboard data. Please try again.')}
          </Alert>
        )}

        {isFetching && (
          <Stack
            spacing="lg"
            role="status"
            aria-busy="true"
            aria-label="Loading outstanding reports and milestones"
            mb="md"
          >
            <OutstandingSectionToolbarSkeleton />
            {Array.from({ length: OUTSTANDING_SKELETON_CARD_COUNT }, (_, i) => (
              <OutstandingCardSkeleton key={i} />
            ))}
          </Stack>
        )}

        {!isFetching && !isError && buckets.length === 0 && (
          <Alert icon={<IconCheck size={16} />} title="All reports and milestones are up to date!" color="green">
            No outstanding reports require action.
          </Alert>
        )}

        {!isFetching && !isError && buckets.length > 0 && (
          <>
            <SimpleGrid cols={1} spacing="lg">
              {buckets.map((bucket) => (
                <QuarterlyReportSummary
                  key={`${bucket.component_project_id}-${bucket.reporting_period.reporting_year}-${bucket.reporting_period.reporting_period_type}-${bucket.reporting_period.reporting_period_value}`}
                  bucket={bucket}
                  onViewReport={handleViewReport}
                  onViewMilestone={handleViewMilestones}
                />
              ))}
            </SimpleGrid>
            {pagination && pagination.total_pages > 1 && (
              <OutstandingPaginationBar
                page={page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total_items}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                disabled={isFetching}
                onChange={handlePageChange}
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
}
